<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import SalesPartyActionsMenu from './SalesPartyActionsMenu.vue';
import SalesPartyGroupToggle from './SalesPartyGroupToggle.vue';
import SalesPartyProgressBar from './SalesPartyProgressBar.vue';
import SalesPartyRepresentatives from './SalesPartyRepresentatives.vue';
import SalesPartyStatusChips from './SalesPartyStatusChips.vue';

defineProps({
  companies: { type: Array, default: () => [] },
  progressStatus: { type: Function, required: true },
});

const emit = defineEmits(['addRepresentative', 'edit', 'remove']);
const { t } = useI18n();
const expanded = ref(true);
const toggle = () => {
  expanded.value = !expanded.value;
};
</script>

<template>
  <div>
    <section v-if="companies.length" class="grid gap-3">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <h2 class="text-base font-semibold text-lais-text">
          {{ t('SALES_PARTIES.COMMON.LEGAL_PEOPLE') }}
        </h2>
        <div class="flex items-center gap-2">
          <span
            class="rounded-full bg-lais-bg-soft px-3 py-1 text-xs font-semibold text-lais-text-muted"
          >
            {{
              t('SALES_PARTIES.LIST.COMPANY_COUNT', {
                count: companies.length,
                company:
                  companies.length === 1
                    ? t('SALES_PARTIES.COMMON.COMPANY')
                    : t('SALES_PARTIES.COMMON.COMPANIES'),
              })
            }}
          </span>
          <SalesPartyGroupToggle
            data-test="companies-group-toggle"
            :expanded="expanded"
            :group-label="t('SALES_PARTIES.COMMON.LEGAL_PEOPLE')"
            @toggle="toggle"
          />
        </div>
      </div>
      <div
        v-if="expanded"
        data-test="companies-group-content"
        class="grid gap-4"
      >
        <LaisCard
          v-for="company in companies"
          :key="company.id"
          :title="company.display_name"
          :prefix-icon="
            Number(company.progress?.percent) === 100
              ? 'i-lucide-check'
              : 'i-lucide-user-round'
          "
        >
          <template #titleAction>
            <SalesPartyActionsMenu
              company
              @edit="emit('edit', company)"
              @remove="emit('remove', company)"
            />
          </template>
          <div class="grid gap-4">
            <div class="flex items-start justify-between gap-3">
              <SalesPartyStatusChips
                :percent="Number(company.progress?.percent || 0)"
                :status="progressStatus(company)"
                :type-label="t('SALES_PARTIES.LIST.LEGAL_BADGE')"
              />
              <span class="font-bold text-lais-primary">
                {{
                  t('SALES_PARTIES.COMMON.PERCENT', {
                    value: company.progress?.percent || 0,
                  })
                }}
              </span>
            </div>
            <SalesPartyProgressBar
              :label="company.display_name"
              :value="Number(company.progress?.percent || 0)"
            />
            <SalesPartyRepresentatives
              :company="company"
              :progress-status="progressStatus"
              @add="emit('addRepresentative', company)"
              @edit="emit('edit', $event)"
              @remove="emit('remove', $event)"
            />
          </div>
        </LaisCard>
      </div>
    </section>
  </div>
</template>
